/**
 * 사례개념화 Revision 도구 (MASTER §9, TEST_PLAN §6).
 * 새 revision은 직전 revision을 복제해서 시작한다. 같은 가설은 같은 id를 유지한다 (Amendment A5).
 */
import type { AnalysisItem, CaseFormulationRevision } from '../schema/v1';

export const FORMULATION_LIST_FIELDS = [
  'currentProblems',
  'precipitatingFactors',
  'predisposingFactors',
  'perpetuatingFactors',
  'protectiveFactors',
] as const;

export const FORMULATION_ITEM_FIELDS = [
  'coreEmotions',
  'coreCognitiveThemes',
  'beliefHypotheses',
  'behavioralPatterns',
  'relationalPatterns',
  'allianceFactors',
  'therapyInterferingPatterns',
] as const;

export const FORMULATION_LABELS: Record<
  (typeof FORMULATION_LIST_FIELDS)[number] | (typeof FORMULATION_ITEM_FIELDS)[number],
  string
> = {
  currentProblems: '현재 주요 문제',
  precipitatingFactors: '촉발요인',
  predisposingFactors: '취약요인',
  perpetuatingFactors: '유지요인',
  protectiveFactors: '보호요인',
  coreEmotions: '핵심 정서',
  coreCognitiveThemes: '핵심 인지 주제',
  beliefHypotheses: '신념 가설',
  behavioralPatterns: '행동 패턴',
  relationalPatterns: '관계 패턴',
  allianceFactors: '상담관계 요인',
  therapyInterferingPatterns: '상담 방해 패턴',
};

/** 화면에서 칸을 묶어 보여주는 순서와 칸마다 무엇을 쓰는지 */
export const FORMULATION_GROUPS = [
  {
    key: 'factors',
    title: '문제와 요인',
    desc: '지금 무엇이 문제이고, 무엇이 그것을 일으키고·만들고·이어가게 하며, 무엇이 버티게 해 주는지',
    fields: [
      'currentProblems',
      'precipitatingFactors',
      'predisposingFactors',
      'perpetuatingFactors',
      'protectiveFactors',
    ],
  },
  {
    key: 'understanding',
    title: '내담자 이해',
    desc: '반복되는 정서·생각·신념·행동·관계 방식 (가설은 사실이 아니에요 — 확인된 것만 확인됨으로)',
    fields: ['coreEmotions', 'coreCognitiveThemes', 'beliefHypotheses', 'behavioralPatterns', 'relationalPatterns'],
  },
  {
    key: 'relationship',
    title: '상담 관계',
    desc: '상담자와의 관계에서 보이는 것',
    fields: ['allianceFactors', 'therapyInterferingPatterns'],
  },
] as const;

export const FORMULATION_HELP: Record<keyof typeof FORMULATION_LABELS, string> = {
  currentProblems: '지금 상담에서 다루는 주된 어려움',
  precipitatingFactors: '이번 어려움이 시작되거나 심해진 계기',
  predisposingFactors: '오래전부터 어려움에 취약하게 만든 배경 (성장 환경, 기질 등)',
  perpetuatingFactors: '어려움이 계속 이어지게 하는 것 (회피, 반복되는 생각, 환경 등)',
  protectiveFactors: '어려움을 버티게 해 주는 강점·자원 (지지체계, 신앙, 대처능력 등)',
  coreEmotions: '반복해서 드러나는 중심 정서',
  coreCognitiveThemes: '반복되는 생각의 주제',
  beliefHypotheses: '자신·타인·세상에 대한 깊은 신념에 관한 가설',
  behavioralPatterns: '반복되는 행동 방식',
  relationalPatterns: '사람들과 관계 맺는 반복 방식',
  allianceFactors: '상담 관계를 돕거나 어렵게 하는 것',
  therapyInterferingPatterns: '상담 진행을 방해하는 패턴 (회피, 취소, 주제 돌리기 등)',
};

export type FormulationContent = Omit<
  CaseFormulationRevision,
  | 'id'
  | 'entityType'
  | 'schemaVersion'
  | 'revision'
  | 'createdAt'
  | 'updatedAt'
  | 'createdBy'
  | 'updatedBy'
  | 'lastModifiedDevice'
  | 'deletedAt'
>;

/** 직전 revision을 바탕으로 새 제안 내용 (변경 기록 필드는 비움) */
export function nextProposal(
  prev: CaseFormulationRevision | undefined,
  base: { clientId: string; formulationId: string; version: number; basedOnSessionIds: string[] },
): FormulationContent {
  // 직전 revision에서 폐기된 가설은 다음 revision으로 넘기지 않는다 (이력에는 그대로 남는다)
  const rejected = new Set(prev?.rejectedHypotheses ?? []);
  const carry = prev
    ? Object.fromEntries([
        ...FORMULATION_LIST_FIELDS.filter((k) => prev[k] !== undefined).map((k) => [k, structuredClone(prev[k])]),
        ...FORMULATION_ITEM_FIELDS.filter((k) => prev[k] !== undefined).map((k) => [
          k,
          structuredClone(prev[k]!.filter((i) => !rejected.has(i.id))),
        ]),
        ...(prev.theoryFormulations ? [['theoryFormulations', structuredClone(prev.theoryFormulations)]] : []),
      ])
    : {};
  return {
    ...carry,
    ...base,
    ...(prev ? { previousRevisionId: prev.id } : {}),
    status: 'proposal',
    approvedByCounselor: false,
  };
}

export type ItemChange = { id: string; text: string; field: string; change: 'added' | 'removed' | 'edited' | 'status' };

/** 두 revision의 차이 (화면의 "이전 버전과 달라진 점") */
export function diffRevisions(a: CaseFormulationRevision, b: CaseFormulationRevision) {
  const lists = FORMULATION_LIST_FIELDS.map((f) => {
    const before = new Set(a[f] ?? []);
    const after = new Set(b[f] ?? []);
    return {
      field: f,
      added: [...after].filter((x) => !before.has(x)),
      removed: [...before].filter((x) => !after.has(x)),
    };
  }).filter((d) => d.added.length || d.removed.length);

  const items: ItemChange[] = [];
  for (const f of FORMULATION_ITEM_FIELDS) {
    const before = new Map<string, AnalysisItem>((a[f] ?? []).map((i) => [i.id, i]));
    const after = new Map<string, AnalysisItem>((b[f] ?? []).map((i) => [i.id, i]));
    for (const [id, i] of after) {
      const old = before.get(id);
      if (!old) items.push({ id, text: i.text, field: f, change: 'added' });
      else if (old.text !== i.text) items.push({ id, text: i.text, field: f, change: 'edited' });
      else if (old.status !== i.status) items.push({ id, text: i.text, field: f, change: 'status' });
    }
    for (const [id, i] of before) if (!after.has(id)) items.push({ id, text: i.text, field: f, change: 'removed' });
  }
  return { lists, items };
}

/** 모든 revision에 걸친 가설 목록 (id 기준) — 가설의 생애 추적용 */
export function hypothesisTimeline(revisions: CaseFormulationRevision[]) {
  const sorted = [...revisions].sort((x, y) => x.version - y.version);
  const map = new Map<string, { id: string; text: string; events: { version: number; event: string }[] }>();
  for (const r of sorted) {
    const mark = (ids: string[] | undefined, event: string) =>
      (ids ?? []).forEach((id) => {
        const e = map.get(id) ?? { id, text: '', events: [] };
        e.events.push({ version: r.version, event });
        map.set(id, e);
      });
    for (const f of FORMULATION_ITEM_FIELDS)
      for (const i of r[f] ?? []) {
        const e = map.get(i.id) ?? { id: i.id, text: i.text, events: [] };
        e.text = i.text;
        map.set(i.id, e);
      }
    mark(r.addedHypotheses, 'added');
    mark(r.strengthenedHypotheses, 'strengthened');
    mark(r.weakenedHypotheses, 'weakened');
    mark(r.rejectedHypotheses, 'rejected');
  }
  return [...map.values()];
}

// ---- AI 의견 중 기존 가설과 연결하지 못한 것: 개정안의 변경 이유에 한 줄씩 두고, 상담자가 화면에서 정리한다 ----
export const AI_PENDING_PREFIX = '[AI 의견·연결 필요]';
export type PendingKind = 'strengthened' | 'weakened' | 'rejected';
export const PENDING_KIND_LABEL: Record<PendingKind, string> = {
  strengthened: '강화',
  weakened: '약화',
  rejected: '폐기',
};
export type PendingOpinion = { kind: PendingKind; text: string; line: string };

export function pendingLine(kind: PendingKind, text: string): string {
  return `${AI_PENDING_PREFIX} ${PENDING_KIND_LABEL[kind]}: ${text.replace(/\s*\n\s*/g, ' ').trim()}`;
}

export function parsePendingOpinions(changeReason: string | undefined): PendingOpinion[] {
  const byLabel = Object.fromEntries(Object.entries(PENDING_KIND_LABEL).map(([k, v]) => [v, k as PendingKind]));
  return (changeReason ?? '')
    .split('\n')
    .map((line) => line.trim())
    .flatMap((line) => {
      if (!line.startsWith(AI_PENDING_PREFIX)) return [];
      const m = /^(강화|약화|폐기):\s*(.*)$/.exec(line.slice(AI_PENDING_PREFIX.length).trim());
      return m ? [{ kind: byLabel[m[1]!]!, text: m[2]!, line }] : [];
    });
}

export function removePendingLine(changeReason: string | undefined, line: string): string | undefined {
  const rest = (changeReason ?? '')
    .split('\n')
    .filter((l) => l.trim() !== line)
    .join('\n')
    .trim();
  return rest || undefined;
}
